import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

function UserProfile({ showToast }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, updateUserProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || '',
    department: currentUser?.department || '',
    employeeCode: currentUser?.employeeCode || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = () => {
    if (updateUserProfile) {
      updateUserProfile(formData);
      showToast('Cập nhật thông tin thành công!', 'success');
      setIsEditing(false);
    } else {
      showToast('Tính năng cập nhật sẽ được triển khai trong phiên bản tới', 'info');
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
      department: currentUser?.department || '',
      employeeCode: currentUser?.employeeCode || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-person-circle me-2"></i>
                Thông tin cá nhân
              </h2>
              <p className="text-muted mb-0">Quản lý thông tin tài khoản của bạn</p>
            </div>
            <button 
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div 
                  className="rounded-circle bg-primary bg-gradient text-white d-inline-flex align-items-center justify-content-center"
                  style={{ width: '120px', height: '120px', fontSize: '3rem' }}
                >
                  <i className="bi bi-person-fill"></i>
                </div>
              </div>
              <h4 className="mb-1">{currentUser?.name}</h4>
              <p className="text-muted mb-2">{currentUser?.email}</p>
              <span className="badge bg-success bg-gradient px-3 py-2">
                <i className="bi bi-shield-check me-1"></i>
                {currentUser?.role}
              </span>
              <hr className="my-4" />
              <div className="d-grid gap-2">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/change-password')}
                >
                  <i className="bi bi-key me-2"></i>
                  Đổi mật khẩu
                </button>
                <button className="btn btn-outline-secondary">
                  <i className="bi bi-download me-2"></i>
                  Tải thông tin
                </button>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Thông tin bổ sung
              </h6>
              <div className="mb-3">
                <small className="text-muted d-block">Trạng thái</small>
                <span className="badge bg-success">
                  <i className="bi bi-check-circle me-1"></i>
                  {currentUser?.status || 'Hoạt động'}
                </span>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Chi nhánh quản lý</small>
                <div className="mt-1">
                  {currentUser?.managedBranches?.includes('all') ? (
                    <span className="badge bg-primary">Tất cả chi nhánh</span>
                  ) : (
                    currentUser?.managedBranches?.map((branch, idx) => (
                      <span key={idx} className="badge bg-info me-1 mb-1">{branch}</span>
                    ))
                  )}
                </div>
              </div>
              <div>
                <small className="text-muted d-block">Ngày tham gia</small>
                <span>{new Date().toLocaleDateString('vi-VN')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-person-badge me-2"></i>
                Chi tiết thông tin
              </h5>
              {!isEditing ? (
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => setIsEditing(true)}
                >
                  <i className="bi bi-pencil me-2"></i>
                  Chỉnh sửa
                </button>
              ) : (
                <div>
                  <button 
                    className="btn btn-success btn-sm me-2"
                    onClick={handleSave}
                  >
                    <i className="bi bi-check-lg me-2"></i>
                    Lưu
                  </button>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={handleCancel}
                  >
                    <i className="bi bi-x-lg me-2"></i>
                    Hủy
                  </button>
                </div>
              )}
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">
                    <i className="bi bi-hash me-1"></i>
                    Mã nhân viên
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="employeeCode"
                    value={formData.employeeCode}
                    disabled={!isEditing}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    <i className="bi bi-person me-1"></i>
                    Họ và tên
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    disabled={!isEditing}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    <i className="bi bi-envelope me-1"></i>
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    disabled={!isEditing}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    <i className="bi bi-telephone me-1"></i>
                    Số điện thoại
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    disabled={!isEditing}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    <i className="bi bi-building me-1"></i>
                    Phòng ban
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="department"
                    value={formData.department}
                    disabled={!isEditing}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    <i className="bi bi-shield-check me-1"></i>
                    Vai trò
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentUser?.role || ''}
                    disabled
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">
                    <i className="bi bi-geo-alt me-1"></i>
                    Chi nhánh
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentUser?.managedBranches?.includes('all') 
                      ? 'Tất cả chi nhánh' 
                      : currentUser?.managedBranches?.join(', ') || 'Chưa có'}
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mt-4">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-activity me-2"></i>
                Hoạt động gần đây
              </h5>
            </div>
            <div className="card-body">
              <div className="list-group list-group-flush">
                <div className="list-group-item px-0">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-success bg-opacity-10 text-success rounded-circle p-2">
                        <i className="bi bi-box-arrow-in-right"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-0">Đăng nhập thành công</h6>
                      <small className="text-muted">Hôm nay, {new Date().toLocaleTimeString('vi-VN')}</small>
                    </div>
                  </div>
                </div>
                <div className="list-group-item px-0">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-2">
                        <i className="bi bi-file-earmark-text"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-0">Xem báo cáo</h6>
                      <small className="text-muted">Hôm qua, 14:30</small>
                    </div>
                  </div>
                </div>
                <div className="list-group-item px-0">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-warning bg-opacity-10 text-warning rounded-circle p-2">
                        <i className="bi bi-pencil"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="mb-0">Cập nhật thông tin</h6>
                      <small className="text-muted">2 ngày trước</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
